import { useId, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  emailCertificate,
  listDeliveries,
  markCertificatePrinted,
  retryCertificateRender,
  type CertificateDetail,
  type Delivery,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { formatDate } from "@/components/format";
import { ErrorBanner, StatusBadge, ToolbarButton } from "@/components/ui";

export function DistributionPanel({ cert }: { cert: CertificateDetail }) {
  const headingId = useId();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const deliveries = useQuery({
    queryKey: ["certificate-deliveries", cert.id],
    queryFn: () => listDeliveries({ q: cert.doc_control_id, limit: 50 }),
  });

  const refreshDeliveries = () => queryClient.invalidateQueries({ queryKey: ["certificate-deliveries", cert.id] });
  const [result, setResult] = useState("");
  const email = useMutation({
    mutationFn: () => emailCertificate(cert.id),
    onMutate: (): void => startAction(print, retry),
    onSuccess: (r) => finish(`Email queued to ${r.recipient}.`),
  });
  const print = useMutation({
    mutationFn: () => markCertificatePrinted(cert.id),
    onMutate: (): void => startAction(email, retry),
    onSuccess: () => finish("Marked as printed."),
  });
  const retry = useMutation({
    mutationFn: () => retryCertificateRender(cert.id),
    onMutate: (): void => startAction(email, print),
    onSuccess: async () => {
      finish("Render queued.");
      await queryClient.invalidateQueries({ queryKey: ["certificate", cert.id] });
    },
  });

  // The status line and error describe only the latest action.
  function startAction(...others: { reset: () => void }[]) {
    setResult("");
    others.forEach((m) => m.reset());
  }
  function finish(message: string) {
    setResult(message);
    return refreshDeliveries();
  }

  const mine = (deliveries.data?.items ?? [])
    .filter((d) => d.certificate_id === cert.id)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const latestEmail = mine.find((d) => d.channel === "email");
  const latestPrint = mine.find((d) => d.channel === "print");

  const canEmail = can("certificate.send") && cert.status === "generated";
  const canPrint = can("certificate.print") && cert.status === "generated";
  const canRetry =
    can("certificate.reissue") && cert.is_reissue && (cert.status === "failed" || cert.status === "pending");

  if (!can("certificate.send") && !can("certificate.print") && !can("certificate.reissue") && mine.length === 0) {
    return null;
  }

  const busy = email.isPending || print.isPending || retry.isPending;
  const line = (d: Delivery | undefined, label: string, withRecipient: boolean) => (
    <p className="flex flex-wrap items-center gap-2 text-zinc-700">
      <span className="font-medium">{label}:</span>
      {d ? (
        <>
          <StatusBadge status={d.status} />
          <span>
            {withRecipient ? `${d.recipient} · ` : ""}
            {formatDate(d.created_at)}
          </span>
        </>
      ) : (
        <span className="text-zinc-500">None yet</span>
      )}
    </p>
  );

  return (
    <section aria-labelledby={headingId} className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 id={headingId} className="text-sm font-semibold">
        Send and print
      </h2>
      <ErrorBanner error={deliveries.error} />
      <ErrorBanner error={email.error ?? print.error ?? retry.error} />
      <div className="space-y-1 text-sm">
        {line(latestEmail, "Email", true)}
        {line(latestPrint, "Print", false)}
      </div>
      {canEmail || canPrint || canRetry ? (
        <div className="flex flex-wrap gap-2">
          {canEmail ? (
            <ToolbarButton disabled={busy} onClick={() => email.mutate()}>
              Email to member
            </ToolbarButton>
          ) : null}
          {canPrint ? (
            <ToolbarButton disabled={busy} onClick={() => print.mutate()}>
              Mark as printed
            </ToolbarButton>
          ) : null}
          {canRetry ? (
            <ToolbarButton disabled={busy} onClick={() => retry.mutate()}>
              Retry render
            </ToolbarButton>
          ) : null}
        </div>
      ) : null}
      <p role="status" className="text-xs text-emerald-700">
        {result}
      </p>
      <Link to="/deliveries" search={{ q: cert.doc_control_id }} className="text-sm font-medium text-zinc-950 underline">
        See deliveries
      </Link>
    </section>
  );
}
